"use client";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import PromotionCardLight from "../components/home_components/PromotionCardLight";
import LocationDisplay from "../components/home_components/Locations";
import { isAuthenticated } from "../utils/helpers";
import { GetProfile } from "../services/profile";

function LeftSidebar({ viewportHeight }) {
  // Get current location to determine active route
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [userData, setUserData] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Helper function to determine if a link is active
  const isActive = path => {
    if (path === "/" && currentPath === "/") {
      return true;
    }
    return path !== "/" && currentPath.startsWith(path);
  };

  // Fetch user profile data for avatar
  useEffect(() => {
    async function fetchProfile() {
      if (isAuthenticated()) {
        try {
          const profile = await GetProfile();
          setUserData(profile.data);
          const stringifiedProfile=JSON.stringify(profile.data)
          localStorage.setItem('profile',stringifiedProfile)
        } catch (error) {
          console.error("Error fetching profile:", error);
          // Don't show error toast in sidebar, just fail silently
        }
      }
    }
    if (!userData) {
      fetchProfile();
    }
  }, []);

  // Handle scroll event to add blur effect to header
  const handleScroll = e => {
    const scrollTop = e.target.scrollTop;
    setIsScrolled(scrollTop > 0);
  };

  // Navigation items
  const navItems = [
    { path: "/home", label: "Home", icon: "/images/home_icon.svg" },
    { path: "/discover", label: "Discover", icon: "/images/search_icon.svg" },
    { path: "/scoops", label: "Scoops", icon: "/images/scoops_icon.svg" },
    {
      path: "/makeyaari-onboarding",
      label: "Yaari",
      icon: "/images/chat.png"
    },
    {
      path: "/classifieds",
      label: "Classifieds",
      icon: "/images/classfields_icon.svg"
    },
    {
      path: "/classifieds",
      label: "Roommate",
      icon: "/images/roommate.png",
      isRoommate: true
    },
    { path: "/more", label: "More", icon: "/images/more_icon.svg" }
  ];

  const handleRoommateClick = () => {
    // Navigate to classifieds page with roommate category
    navigate("/classifieds?category=Roommate");
  };

  const handleProfileClick = () => {
    if (!isAuthenticated()) {
      localStorage.setItem("target", "/profile");
      navigate("/signin");
      return;
    } else {
      navigate("/profile");
    }
  };

  const sidebarStyles = {
    height: viewportHeight,
    position: "sticky",
    top: 0,
    overflowY: "auto",
    overflowX: "hidden",
    background:
      "linear-gradient(to bottom, #ffe9f3, #ffe1e9, #ffc8ce, #ffd7e6)",
    WebkitOverflowScrolling: "touch",
    msOverflowStyle: "none",
    scrollbarWidth: "none"
  };

  return (
    <aside
      className="col-span-4 lg:col-span-2 xl:col-span-2 border-r font-afacad sidebar-scrollable"
      style={sidebarStyles}
    >
      <style>{`
        .sidebar-scrollable::-webkit-scrollbar {
          display: none;
        }
        .sidebar-scrollable {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .header-blur {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          background: rgba(255, 233, 243, 0.8);
          transition: all 0.3s ease;
        }
        .header-no-blur {
          background: linear-gradient(to bottom, #ffe9f3, #ffe1e9);
          transition: all 0.3s ease;
        }
      `}</style>

      <div className="flex flex-col min-h-full">
        {/* Location Header - Fixed at top */}
        <div
          className={`p-4 border-b sticky top-0 z-10 ${
            isScrolled ? "header-blur" : "header-no-blur"
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Left side - Logo and Brand */}
            <div className="flex items-center">
              <img
                src="/images/ping-desi-logo.png"
                alt="Gallyo logo"
                width={20}
                height={20}
                className="object-contain mr-2"
              />
              <div className="font-semibold text-[#7B189F]">Gallyo</div>
            </div>

            {/* Right side - Profile Avatar */}
            <button
              onClick={handleProfileClick}
              className="w-8 h-8 rounded-full overflow-hidden border-2 border-[#7B189F] hover:border-purple-700 transition-colors cursor-pointer bg-pink-100"
              title="Profile"
            >
              <img
                src={
                  userData?.profileImage ||
                  userData?.avatarurl ||
                  "/placeholder.svg"
                }
                alt="Profile Avatar"
                className="w-full h-full object-cover"
              />
            </button>
          </div>

          {/* Location Display */}
          <div className="text-sm text-gray-700 pl-7 mt-1">
            <LocationDisplay />
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Navigation Menu */}
          <nav className="py-4">
            <div className="space-y-1 px-3">
              {navItems.map(item => {
                const isRoommate = item.isRoommate;
                return isRoommate ? (
                  <button
                    key={item.path + item.label}
                    onClick={handleRoommateClick}
                    className={`flex items-center gap-3 text-sm py-2 px-3 rounded-md w-full text-left transition-colors ${
                      isActive(item.path) && currentPath.includes("classifieds")
                        ? "bg-white"
                        : "hover:bg-white"
                    }`}
                  >
                    <img
                      src={item.icon || "/placeholder.svg"}
                      alt={`${item.label} icon`}
                      className="w-5 h-5 flex-shrink-0"
                    />
                    <span>{item.label}</span>
                  </button>
                ) : (
                  <Link
                    key={item.path + item.label}
                    to={item.path}
                    className={`flex items-center gap-3 text-sm py-2 px-3 rounded-md transition-colors ${
                      isActive(item.path) ? "bg-white" : "hover:bg-white"
                    }`}
                  >
                    <img
                      src={item.icon || "/placeholder.svg"}
                      alt={`${item.label} icon`}
                      className="w-5 h-5 flex-shrink-0"
                    />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Promotion Card */}
          <div className="px-3 pb-4">
            <PromotionCardLight />
          </div>
        </div>
      </div>
    </aside>
  );
}

export default LeftSidebar;
